import { create } from 'zustand';
import type { BanquetteSlot, BanquettePolyline, PanelRole } from '@estimator/shared';

const SLOT_COLORS = [
  '#e74c3c', '#3498db', '#2ecc71', '#9b59b6', '#f39c12',
  '#1abc9c', '#e67e22', '#e91e63', '#00bcd4', '#8bc34a',
];

let slotCounter = 0;

interface BanquetteSlotStore {
  slots: BanquetteSlot[];
  activeSlotId: string | null;

  // Actions
  setSlots: (slots: BanquetteSlot[]) => void;
  setActiveSlot: (id: string | null) => void;
  addSlot: (role?: PanelRole) => void;
  updateSlot: (id: string, updates: Partial<BanquetteSlot>) => void;
  removeSlot: (id: string) => void;
  addPolylineToSlot: (slotId: string, polyline: BanquettePolyline) => void;
  removePolyline: (slotId: string, polylineIndex: number) => void;
  clearAll: () => void;
}

export const useBanquetteSlotStore = create<BanquetteSlotStore>((set, get) => ({
  slots: [],
  activeSlotId: null,

  setSlots: (slots) => {
    slotCounter = slots.length;
    set({ slots });
  },

  setActiveSlot: (id) => set({ activeSlotId: id }),

  addSlot: (role = 'front') => {
    slotCounter++;
    const colorIndex = (get().slots.length) % SLOT_COLORS.length;
    const newSlot: BanquetteSlot = {
      id: `slot_${slotCounter}_${Date.now()}`,
      name: `Slot ${slotCounter}`,
      material_id: null,
      color: SLOT_COLORS[colorIndex],
      calc_mode: 'linear',
      panel_role: role,
      polylines: [],
      fabric_wrap_value: null,
      fabric_staple_allowance: 1,
    };
    set((state) => ({
      slots: [...state.slots, newSlot],
      activeSlotId: newSlot.id,
    }));
  },

  updateSlot: (id, updates) =>
    set((state) => ({
      slots: state.slots.map((s) => (s.id === id ? { ...s, ...updates } : s)),
    })),

  removeSlot: (id) =>
    set((state) => ({
      slots: state.slots.filter((s) => s.id !== id),
      activeSlotId: state.activeSlotId === id ? null : state.activeSlotId,
    })),

  addPolylineToSlot: (slotId, polyline) =>
    set((state) => ({
      slots: state.slots.map((s) =>
        s.id === slotId
          ? { ...s, polylines: [...s.polylines, polyline] }
          : s,
      ),
    })),

  removePolyline: (slotId, polylineIndex) =>
    set((state) => ({
      slots: state.slots.map((s) =>
        s.id === slotId
          ? { ...s, polylines: s.polylines.filter((_, i) => i !== polylineIndex) }
          : s,
      ),
    })),

  clearAll: () => {
    slotCounter = 0;
    set({ slots: [], activeSlotId: null });
  },
}));
